import { useState } from "react";
import type {
  Command,
  RecordView,
} from "@shared-core/domain/object-ui/contract";
import { useObjectRecord } from "@shared-core/domain/object-ui/hooks";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { useCommandHost } from "../app/useCommandHost";
import { ErrorPanel } from "../app/components/patterns/ErrorPanel";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import { Skeleton } from "../app/components/primitives/skeleton";

// Setup › Portal sign-in (docs/features/portal-sign-in/09 section 5.9): the defaults every customer and supplier starts
// from. The values and counts are the Sign-in default record's, and each change is that record's own command, run in the
// command shell with its dry run, so the page holds no dialog or rule of its own. PROVISIONAL wording
// (docs/ui-wording.md), built from the specs and awaiting review.

type Portal = "customer" | "supplier";

const DEFAULTS_OBJECT = "portal_sign_in.sign_in_default";
const PORTAL_WORDS: Record<Portal, { tab: string; one: string; many: string }> =
  {
    customer: { tab: "Customers", one: "customer", many: "customers" },
    supplier: { tab: "Suppliers", one: "supplier", many: "suppliers" },
  };

interface SettingLine {
  key: string;
  title: string;
}

/** The settings of a portal, grouped as a customer's or supplier's record groups them. */
function groupsOf(
  portal: Portal,
): { heading: string; settings: SettingLine[] }[] {
  return [
    {
      heading:
        portal === "customer" ? "Order contacts" : "Purchase order emails",
      settings:
        portal === "customer"
          ? [
              {
                key: "order_contact_who",
                title: "Who can be an order contact",
              },
              {
                key: "link_mode",
                title: "What an order contact can do from the email",
              },
            ]
          : [
              {
                key: "link_mode",
                title: "What the purchase order email can do",
              },
            ],
    },
    {
      heading: "Portal users",
      settings: [
        { key: "who_can_have_access", title: "Who can have access" },
        // Suppliers have no admins (spec 07), so Who can invite, Who answers requests for access and the access
        // review email are customer settings.
        ...(portal === "customer"
          ? [{ key: "who_can_invite", title: "Who can invite" }]
          : []),
        { key: "ways", title: "Ways to sign in" },
        ...(portal === "customer"
          ? [
              {
                key: "answered_by",
                title: "Who answers requests for access",
              },
            ]
          : []),
        { key: "session_days", title: "Session length" },
        ...(portal === "customer"
          ? [{ key: "access_review_months", title: "Access review email" }]
          : []),
      ],
    },
  ];
}

/** Suppliers have no portal block: a closed supplier is the block (07 section 11). */
function fixedRulesOf(portal: Portal): string[] {
  const one = PORTAL_WORDS[portal].one;
  return [
    "Nobody gets an account without an invite. Invites last 30 days.",
    "Opening an emailed link never acts on its own.",
    "A Microsoft or Google address counts only when the provider has verified it.",
    `Nothing about a ${one} shows until an address is proven.`,
    portal === "customer"
      ? "A blocked customer turns everyone away."
      : "A closed supplier turns everyone away.",
    "A deactivated user is refused everywhere.",
  ];
}

export function PortalSignInSetupPage() {
  const [portal, setPortal] = useState<Portal>("customer");
  return (
    <div className="space-y-5 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <SettingsBreadcrumbs
            crumbs={[{ label: "Setup" }, { label: "Portal sign-in" }]}
          />
          <h1 className="text-2xl font-semibold text-foreground">
            Portal sign-in
          </h1>
          <p className="text-sm text-muted-foreground">
            The defaults every {PORTAL_WORDS[portal].one} starts from. A{" "}
            {PORTAL_WORDS[portal].one} that has changed a setting keeps the
            value it chose.
          </p>
        </div>
        <div role="group" aria-label="Defaults for" className="flex gap-1">
          {(Object.keys(PORTAL_WORDS) as Portal[]).map((p) => (
            <Button
              key={p}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={portal === p}
              onClick={() => setPortal(p)}
              className={
                portal === p
                  ? "h-7 border-primary bg-primary/10 px-2.5 font-semibold text-primary hover:bg-primary/10 hover:text-primary"
                  : "h-7 px-2.5"
              }
            >
              {PORTAL_WORDS[p].tab}
            </Button>
          ))}
        </div>
      </div>
      <PortalDefaults key={portal} portal={portal} />
      <section className="space-y-2">
        <h2 className="text-sm font-semibold">Fixed, not settings</h2>
        <ul className="list-disc space-y-1.5 pl-5 text-sm">
          {fixedRulesOf(portal).map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function PortalDefaults({ portal }: { portal: Portal }) {
  const index = useObjectManifest();
  // Until portal_sign_in_module is built the object exists only in the mock-up draft; a real build says so.
  if (!index.isObjectKnown(DEFAULTS_OBJECT))
    return (
      <Card className="p-5 text-sm text-muted-foreground">
        Portal sign-in isn&apos;t available in One yet.
      </Card>
    );
  return <PortalDefaultsRecord portal={portal} />;
}

function PortalDefaultsRecord({ portal }: { portal: Portal }) {
  const index = useObjectManifest();
  const object = index.objectOf(DEFAULTS_OBJECT);
  const read = useObjectRecord(object, portal);
  const { openCommand, shell } = useCommandHost();
  const words = PORTAL_WORDS[portal];

  if (read.status === "loading")
    return (
      <div
        className="space-y-3"
        aria-busy="true"
        aria-label="Loading the defaults"
      >
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  // 503 until spec 09 builds the sign-in runtime, and wherever its code key is missing: a lasting state, not a fault.
  if (read.status === "error" && read.httpStatus === 503)
    return (
      <Card className="p-5 text-sm text-muted-foreground">
        Portal sign-in isn&apos;t switched on yet.
      </Card>
    );
  if (read.status === "error")
    return read.httpStatus === 404 ? (
      <Card className="p-5 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">
          No defaults have been set up yet.
        </p>
        <p>
          The {words.one} defaults are created when One is set up for portal
          sign-in. Ask engineering to run that set-up.
        </p>
      </Card>
    ) : (
      <ErrorPanel
        message={`Couldn't load the ${words.one} defaults.`}
        onRetry={read.reload}
      />
    );

  const record: RecordView = read.value;
  const total = Number(record.party_count ?? 0);
  const commandOf = (key: string): Command | undefined =>
    record.commands.find(
      (c) => c.name === `portal_sign_in.change_default_${key}`,
    );

  return (
    <div className="space-y-4">
      {groupsOf(portal).map((group) => (
        <section key={group.heading} className="space-y-1.5">
          <h2 className="text-sm font-semibold">{group.heading}</h2>
          <ul className="divide-y divide-border overflow-hidden rounded-[10px] border border-border">
            {group.settings.map((setting) => {
              const command = commandOf(setting.key);
              const own = Number(record[`${setting.key}_own`] ?? 0);
              return (
                <li
                  key={setting.key}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-2 text-sm sm:grid-cols-[minmax(150px,220px)_minmax(0,1fr)_auto]"
                >
                  <p className="col-span-2 text-muted-foreground sm:col-span-1">
                    {setting.title}
                  </p>
                  <p>
                    {String(record[setting.key] ?? "–")}{" "}
                    <span className="text-muted-foreground">
                      · {own} of {total} {words.many} have their own
                    </span>
                  </p>
                  {command && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 font-medium"
                      disabled={!command.enabled}
                      title={command.reason ?? undefined}
                      aria-label={`Change ${setting.title.toLowerCase()}`}
                      onClick={() =>
                        openCommand({
                          endpoint: {
                            command: index.commandOf(command.name),
                            href: command.href,
                            dryRunHref: command.dry_run_href,
                          },
                          context: `Default for every ${words.one}`,
                        })
                      }
                    >
                      Change…
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {shell}
    </div>
  );
}
